import { CheckCircleIcon } from '@heroicons/react/outline'
import StrategyCallButton from './StrategyCallButton'

const COLORS = {
    dark: '#1F2937',
    accent: '#ED5C2F',
    text50: 'color-mix(in oklab, #1f2937 50%, transparent)',
    text70: 'color-mix(in oklab, #1f2937 70%, transparent)',
    text80: 'color-mix(in oklab, #1f2937 80%, transparent)',
    borderSoft: 'color-mix(in oklab, #1f2937 10%, transparent)',
    light: '#FFF8EA',
}

const defaultContent = {
    number: '03',
    eyebrow: 'Strategy & Creative',
    titlePrefix: 'Plans, offers, and creative ',
    titleHighlight: 'built for direct mail.',
    description:
        'Direct mail rewards a different kind of creative than digital. We help teams build campaign plans, sharpen offers, and develop copy and design that reads well on paper, and structure testing so you learn from every drop instead of guessing.',
    includedHeading: "What's included",
    includedItems: [
        'Campaign strategy & planning',
        'Offer development',
        'Direct response copywriting',
        'Mail-format design',
        'Brand-aligned templates',
        'A/B & multivariate test design',
        'Cadence & sequence planning',
        'Creative refresh & iteration',
    ],
    cta: {
        label: 'Talk to our team',
        href: '/contact-us',
    },
    variants: [
        {
            badge: 'Variant A',
            headline: 'Your renewal is coming up.',
            footer: 'Standard offer',
            emphasis: 'muted',
            bars: ['w-full', 'w-4/5', 'w-3/5'],
        },
        {
            badge: 'Winner',
            headline: "You could be paying less. Here's what we found.",
            footer: 'Personalized rate',
            emphasis: 'winner',
            bars: ['w-full', 'w-4/5', 'w-3/5', 'w-2/3'],
            bubble: 'B',
        },
        {
            badge: 'Variant C',
            headline: 'A better rate. A better policy.',
            footer: 'Benefit-led',
            emphasis: 'soft',
            bars: ['w-full', 'w-4/5', 'w-3/5'],
        },
    ],
}

function IncludedItem({ children }) {
    return (
        <div className="flex items-start gap-2">
            <CheckCircleIcon
                className="mt-0.5 h-5 w-5 shrink-0"
                style={{ color: COLORS.accent }}
            />
            <div className="text-sm leading-7" style={{ color: COLORS.text80 }}>
                {children}
            </div>
        </div>
    )
}

function VariantCard({ badge, headline, footer, emphasis, bars, bubble }) {
    const isWinner = emphasis === 'winner'
    const isSoft = emphasis === 'soft'

    const cardStyle = isWinner
        ? {
              backgroundColor: '#ffffff',
              borderColor: COLORS.accent,
              borderWidth: '2px',
          }
        : isSoft
        ? {
              background: 'color-mix(in oklab, #fff8ea 60%, transparent)',
              borderColor: COLORS.borderSoft,
          }
        : {
              backgroundColor: COLORS.light,
              borderColor: COLORS.borderSoft,
          }

    return (
        <div
            className={`relative flex flex-col gap-2 rounded-2xl p-5 shadow-md transition-transform duration-200 ${
                isWinner
                    ? 'hover:-translate-y-2 shadow-xl'
                    : 'hover:-translate-y-1'
            }`}
            style={cardStyle}>
            {bubble ? (
                <div className="absolute -right-3 -top-3 flex h-9 w-9 items-center justify-center rounded-full bg-[#ed5c2f] text-sm font-bold text-white shadow-lg animate-pulse">
                    {bubble}
                </div>
            ) : null}

            <div className="w-fit rounded-full bg-[#ed5c2f]/10 px-2.5 py-1">
                <div
                    className="text-xs font-bold uppercase tracking-[1.2px] leading-7"
                    style={{ color: COLORS.accent }}>
                    {badge}
                </div>
            </div>

            <div
                className={`mt-1 font-bold leading-tight ${
                    isWinner ? 'text-base' : 'text-sm'
                }`}
                style={{ color: COLORS.dark }}>
                {headline}
            </div>

            <div className="mt-2 flex flex-col gap-1.5">
                {bars.map((widthClass, index) => (
                    <div
                        key={`${badge}-${index}`}
                        className={`h-1.5 rounded ${widthClass}`}
                        style={{ backgroundColor: COLORS.borderSoft }}
                    />
                ))}
            </div>

            <div
                className="mt-auto pt-3 text-xs font-semibold uppercase tracking-[1.2px] leading-7"
                style={{
                    color: isWinner ? COLORS.accent : COLORS.text50,
                }}>
                {footer}
            </div>
        </div>
    )
}

export default function StrategyCreativeSection(props) {
    const number = props?.number || defaultContent.number
    const eyebrow = props?.eyebrow || defaultContent.eyebrow
    const titlePrefix = props?.titlePrefix || defaultContent.titlePrefix
    const titleHighlight =
        props?.titleHighlight || defaultContent.titleHighlight
    const description = props?.description || defaultContent.description
    const includedHeading =
        props?.includedHeading || defaultContent.includedHeading
    const includedItems =
        Array.isArray(props?.includedItems) && props.includedItems.length > 0
            ? props.includedItems
            : defaultContent.includedItems
    const cta = props?.cta || defaultContent.cta
    const variants =
        Array.isArray(props?.variants) && props.variants.length > 0
            ? props.variants
            : defaultContent.variants

    return (
        <section id="strategy-creative" className="bg-white scroll-mt-24">
            <div className="container mx-auto px-4 py-16 lg:py-24">
                <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-16">
                    <div className="flex flex-col gap-5">
                        <div className="flex items-center gap-3">
                            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#ed5c2f]">
                                <div className="text-sm font-bold leading-7 text-white">
                                    {number}
                                </div>
                            </div>
                            <div
                                className="text-xs uppercase tracking-[1.2px]"
                                style={{ color: COLORS.text50 }}>
                                {eyebrow}
                            </div>
                        </div>

                        <h2
                            className="text-3xl font-[700] tracking-tight md:text-[44px] md:leading-[1.1]"
                            style={{ color: COLORS.dark }}>
                            {titlePrefix}
                            <span style={{ color: COLORS.accent }}>
                                {titleHighlight}
                            </span>
                        </h2>

                        <p
                            className="text-lg leading-7"
                            style={{ color: COLORS.text70 }}>
                            {description}
                        </p>

                        <div
                            className="mt-2 rounded-2xl border p-6"
                            style={{
                                backgroundColor: COLORS.light,
                                borderColor:
                                    'color-mix(in oklab, #ed5c2f 15%, transparent)',
                            }}>
                            <div
                                className="mb-3 text-xs font-bold uppercase tracking-[1.2px] leading-7"
                                style={{ color: COLORS.accent }}>
                                {includedHeading}
                            </div>
                            <div className="grid grid-cols-1 gap-x-6 gap-y-2 lg:grid-cols-2">
                                {includedItems.map((item, index) => (
                                    <IncludedItem key={`${item}-${index}`}>
                                        {item}
                                    </IncludedItem>
                                ))}
                            </div>
                        </div>

                        {cta?.label ? (
                            <div>
                                <StrategyCallButton className="inline-flex w-fit rounded-full bg-rtl-gradient px-6 py-3.5 text-base font-semibold text-white shadow-lg transition hover:opacity-95">
                                    {cta.label}
                                </StrategyCallButton>
                            </div>
                        ) : null}
                    </div>

                    <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                        {variants.map((variant) => (
                            <VariantCard
                                key={`${variant.badge}-${variant.footer}`}
                                {...variant}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </section>
    )
}
